import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  LEVELS_COMPACT_BELOW, buildGraphModel, graphOutline, layoutGraph, levelsLayout, lineIndexes,
  matchingNodes,
  type GraphHop as GovernedGraphHop, type GraphLayout, type GraphModel, type GraphNode,
} from '@solum/experience'
import { EntityIcon } from '../EntityIcon'
import { Button } from '../controls/Button'
import { Input } from '../controls/Input'
import { cx } from '../controls/cx'
import { readableProblem } from '../controls/readable'
import {
  RelationshipLevels, cardFacts, cardHeightFor, cardStatus, verbRowHeight, type Verb,
} from './RelationshipLevels'
import s from './RelationshipGraph.module.css'

/**
 * The record's whole relationship network, as one governed picture (#2324).
 *
 * Data-free, like every other lens in this directory: hops go in, nodes come
 * back. It fetches nothing, decides no authority, and knows no entity. A record
 * the caller may not read never reaches it, and nothing here could tell.
 *
 * ── TWO SURFACES, ONE MODEL, AND THAT IS THE ACCESSIBILITY ANSWER ──────────
 *
 * §7 asks for "a semantic accessible list/tree alternative describing the same
 * loaded graph". It is drawn BESIDE the canvas rather than hidden behind it, and
 * it is DERIVED from the same model by `graphOutline` (rule 42). The alternative
 * nobody looks at is the one that goes stale — and for an accessible alternative
 * that means the people relying on it are the only ones seeing the old answer.
 *
 * So the `<svg>` is `role="img"` with one accessible name, and every interaction
 * a person can reach with a keyboard lives in the list: the group, the node, and
 * the four verbs. A pointer may also click a node on the canvas; that is the
 * same selection, into the same state.
 *
 * ── LINES ARE NOT COLOURED, AND THAT IS DELIBERATE ─────────────────────────
 *
 * §7: "relationship type/edge meaning is not encoded by color alone." This
 * component takes the strongest available reading of that — meaning is not
 * encoded by colour AT ALL. A line's identity is its index in the governed group
 * order, and the index drives a STROKE PATTERN, the order of the legend and the
 * label drawn at the line's outer end. A person who cannot distinguish two
 * strokes reads the same graph from the list beside it.
 *
 * It is also what keeps this inside the platform's closed token vocabulary:
 * eight categorical hues would have been eight invented tokens, and inventing
 * tokens is how a component stops matching the theme it lives in.
 *
 * ── WHAT IT DOES NOT DO ────────────────────────────────────────────────────
 *
 *   · it never persists a coordinate. §8: a hand-arranged position is not
 *     platform data, so both layouts recompute from the model every time.
 *   · it never crawls. One hop is one `onExpand`, and the host asks the door.
 *   · it never invents a count. A group's number is the caller's own; `loaded`
 *     and `count` can differ and the difference is never attributed.
 */
export interface RelationshipGraphProps {
  /** Every hop the person has asked for, newest last. */
  hops: readonly GraphHop[]
  /** Levels (#2406), Network or Metro — the same model, placed three ways. */
  layout: GraphLayout
  /** The selected node key — a different thing from keyboard focus. */
  selected?: string | null
  onSelect?: (key: string) => void
  /** The four verbs. An absent one is not offered, rather than drawn dead. */
  onPeek?: (node: GraphNode) => void
  onOpen?: (node: GraphNode) => void
  onExpand?: (node: GraphNode) => void
  onMakeRoot?: (node: GraphNode) => void
  /** Collapse one expanded branch, keeping the rest of the loaded graph. */
  onCollapse?: (node: GraphNode) => void
  /** Page one group further. Offered only where the door said there is more. */
  onShowMore?: (slot: string) => void
  /**
   * Put the graph back on the record the page is about (§7's "Reset-root").
   *
   * Supplied by the host ONLY when the root has actually moved — this component
   * has no idea which record the page is about, and a control that was always
   * there would be a dead control most of the time.
   */
  onResetRoot?: () => void
  /** Groups the LEGEND has hidden. A reading choice; it governs nothing. */
  hiddenGroups?: readonly string[]
  onToggleGroup?: (slot: string) => void
  /** The host's own controls — the List/Graph and layout switchers. */
  actions?: ReactNode
  /* ── what only the host knows, for the Levels cards (#2406) ────────────
     Each is optional and each is ABSENT rather than guessed when the host does
     not supply it: a card without an icon shows the generic record glyph, a fact
     without a governed label shows its key humanised, and a band without a
     capability check draws no Add at all. */
  /** The governed glyph of an entity — `core.entity_identity.icon_key`. */
  iconOf?: (entity: string) => string | null | undefined
  /** An edge attribute's governed label, and its option's label where it has one. */
  describeFact?: (slot: string, key: string, value: unknown) => { label: string; value: string } | null
  /** Does this slot of the ROOT offer a create or link write? Only then is its Add drawn. */
  canAdd?: (slot: string) => boolean
  /** Start a relationship in one slot of the root — a band's Add. */
  onAdd?: (slot: string) => void
  /** Start a relationship of any type — the head's Add relationship. */
  onAddRelationship?: () => void
  /** The preview of the selected node, composed by the host from its form. */
  peek?: ReactNode
  busy?: boolean
  /** A refusal or a failure, already worded by the host. */
  problem?: ReactNode
  testId?: string
}

/** Which stroke pattern a line index takes. Four, cycled — see the header. */
const DASHES = ['none', '6 4', '2 4', '10 4 2 4']

const nodeLabel = (node: { label: string | null; entity: string }): string =>
  node.label?.trim()
  // The SAME sentence every other lens over these records uses. A graph that
  // called an unnameable record something different from the list beside it
  // would be two answers to one question.
  || `a ${node.entity.replace(/_/g, ' ')} you cannot open`

/* Where a station's name sits, relative to the station (Metro): see
   `namePlace` in the component. Offsets are the drawing's own units — the same
   ones the layout places stations in — so they scale with the zoom like
   everything else in the picture. */
type NamePlace = 'below' | 'beside' | 'above' | 'belowLeft'
const NAME_ANCHOR: Record<NamePlace, string | undefined> = {
  below: undefined, above: undefined, beside: s.besideName, belowLeft: s.leftName,
}
const nameAt = (place: NamePlace, isRoot: boolean) => ({
  below: { x: 0, y: isRoot ? 30 : 24 },
  beside: { x: 16, y: 4 },
  above: { x: 0, y: isRoot ? -20 : -16 },
  belowLeft: { x: -16, y: isRoot ? 26 : 20 },
})[place]
type LineNamePlace = NamePlace | 'belowFar'
const LINE_NAME_ANCHOR: Record<LineNamePlace, string | undefined> = { ...NAME_ANCHOR, belowFar: undefined }
const lineNameAt = (station: { x: number; y: number }, place: LineNamePlace) => ({
  below: { x: station.x, y: station.y - 18 },
  beside: { x: station.x + 16, y: station.y - 12 },
  above: { x: station.x, y: station.y - 32 },
  belowLeft: { x: station.x - 16, y: station.y + 36 },
  // A dense interchange may have tracks across all four original positions.
  // A second centred row clears record names whose baseline is at +24.
  belowFar: { x: station.x, y: station.y + 44 },
})[place]

export function RelationshipGraph({
  hops, layout, selected = null, onSelect, onPeek, onOpen, onExpand, onMakeRoot,
  onCollapse, onShowMore, onResetRoot, hiddenGroups = [], onToggleGroup, actions, peek,
  busy, problem, testId = 'relationship-graph',
  iconOf, describeFact, canAdd, onAdd, onAddRelationship,
}: RelationshipGraphProps) {
  const [query, setQuery] = useState('')
  const [zoom, setZoom] = useState(1)

  const full = useMemo(() => buildGraphModel(hops), [hops])
  const hidden = useMemo(() => new Set(hiddenGroups), [hiddenGroups])

  /* A hidden group is removed from the MODEL rather than from the picture.
     §7: "legend may hide/show a loaded relationship group without mutating the
     governed definition" — so nothing is written anywhere, and the layout that
     places what is left has one fewer line to route around instead of leaving a
     gap where a hidden line used to be. */
  const model: GraphModel = useMemo(() => {
    if (hidden.size === 0) return full
    const kept = full.edges.filter((e) => !hidden.has(e.slot))
    const reachable = new Set<string>(['root'])
    for (const e of kept) { reachable.add(e.source); reachable.add(e.target) }
    return {
      ...full,
      edges: kept,
      nodes: new Map([...full.nodes].filter(([key]) => reachable.has(key))),
    }
  }, [full, hidden])

  /* Network and Metro share one placement shape; Levels has its own. The first
     is computed under Levels too — it is sub-millisecond and it keeps every line
     below it about the two older pictures exactly as it was. */
  const placement = useMemo(
    () => layoutGraph(model, layout === 'metro' ? 'metro' : 'network'), [model, layout])

  /* THE WIDTH THE LEVELS PICTURE HAS, and the one decision it drives: side by
     side, or stacked. Measured on the canvas rather than read off the window,
     for the reason the container query below gives — this graph lives in a
     record column whose width has nothing to do with the window's. Only the
     arrangement depends on it; what is drawn never does. A DOM with no layout
     engine measures 0, which is read as "unknown" and draws side by side. */
  const canvasRef = useRef<HTMLDivElement>(null)
  const [canvasWidth, setCanvasWidth] = useState(0)
  useEffect(() => {
    const el = canvasRef.current
    if (!el || layout !== 'levels') return
    const measure = () => setCanvasWidth((prev) => (prev === el.clientWidth ? prev : el.clientWidth))
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [layout])
  const compact = canvasWidth > 0 && canvasWidth < LEVELS_COMPACT_BELOW
  /* The primary pointer, read once: a coarse one needs the 44px floor on the
     chosen card's verbs, and the layout has to reserve that row's height. */
  const [coarse] = useState(() => typeof window !== 'undefined'
    && window.matchMedia?.('(pointer: coarse)').matches === true)
  const edgeByKey = useMemo(() => new Map(full.edges.map((e) => [e.key, e] as const)), [full.edges])
  const levels = useMemo(() => (layout !== 'levels' ? null : levelsLayout(model, {
    compact,
    width: canvasWidth,
    hidden,
    cardHeight: (key, via) => cardHeightFor(
      cardFacts(edgeByKey.get(via), describeFact).length,
      // Only the chosen card grows, and only when it has verbs to show.
      key === selected && (onPeek || onOpen || onExpand || onCollapse || onMakeRoot) ? verbRowHeight(coarse) : 0),
  })), [layout, model, compact, canvasWidth, hidden, edgeByKey, describeFact, selected, coarse,
    onPeek, onOpen, onExpand, onCollapse, onMakeRoot])
  const outline = useMemo(() => graphOutline(full), [full])
  const lines = useMemo(() => lineIndexes(full.groups), [full.groups])
  const matches = useMemo(() => matchingNodes(model, query), [model, query])

  const selectedNode = selected ? full.nodes.get(selected) ?? null : null
  const select = useCallback((key: string) => onSelect?.(key), [onSelect])

  /* The verbs of ONE node, as a function: the bar offers them for the selected
     node, and the Levels cards offer the same list — one list, so no layout can
     offer a verb another does not. */
  const verbsOf = (node: GraphNode): Verb[] => (node.key === 'root' ? [] : [
    onPeek && { key: 'peek', label: 'Peek', run: () => onPeek(node) },
    onOpen && { key: 'open', label: 'Open full record', short: 'Open', run: () => onOpen(node) },
    full.expanded.includes(node.key)
      ? onCollapse && { key: 'collapse', label: 'Collapse', run: () => onCollapse(node) }
      : onExpand && { key: 'expand', label: 'Expand relationships', short: 'Expand', run: () => onExpand(node) },
    onMakeRoot && { key: 'root', label: 'Make root', run: () => onMakeRoot(node) },
  ].filter(Boolean) as Verb[])
  const verbs = selectedNode ? verbsOf(selectedNode) : []

  /* What the head says about the picture — every number the caller's own.
     The CONNECTIONS figure is a sum of the door's counts and is shown only when
     every group it would add up was counted; a group nobody measured makes the
     total unknowable, and a total that quietly skipped it would be a smaller
     number presented as the whole. */
  const drawnGroups = full.groups.filter((g) =>
    g.count !== 0 || full.edges.some((e) => e.slot === g.slot))
  const counted = drawnGroups.every((g) => g.count !== null)
  const connections = drawnGroups.reduce((n, g) => n + (g.count ?? 0), 0)
  const deepest = [...full.nodes.values()].reduce((d, n) => Math.max(d, n.depth), 0)

  /* A choice made in the accessible tree lands on a card that may be scrolled
     out of sight; bring its verbs into view. `nearest` moves nothing when they
     are already visible, so choosing a card on screen does not jump. */
  useEffect(() => {
    if (layout !== 'levels' || !selected) return
    const bar = canvasRef.current?.querySelector(`[data-testid="${testId}-verbs"]`)
    if (bar && typeof bar.scrollIntoView === 'function') bar.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [layout, selected, testId])

  /* The edges TOUCHING the selected node, in the loaded graph rather than the
     visible one: a person who hid a line in the legend has hidden the picture,
     not the fact. */
  const selectedEdges = useMemo(
    () => (selected ? full.edges.filter((e) => e.source === selected || e.target === selected) : []),
    [full.edges, selected])

  /* THE LINE NAMES, one label per STATION rather than one per line.
     Two relationship types that end at the same record — the fixture's
     `parent_of` and `supplier_to` both terminate at `g5` — used to draw two
     names at one coordinate, which read as "SBpplied by": neither name, and a
     reader could not tell there were two. An interchange is one place with
     several lines through it, so it carries ONE label that names them all. */
  const lineNames = useMemo(() => {
    const at = new Map<string, { x: number; y: number; names: string[] }>()
    for (const g of full.groups) {
      if (hidden.has(g.slot)) continue
      const outer = model.edges
        .filter((e) => e.slot === g.slot)
        .map((e) => (e.source === 'root' ? e.target : e.source))
        .map((key) => ({ key, p: placement.nodes.get(key) }))
        .filter((o): o is { key: string; p: NonNullable<typeof o.p> } => !!o.p)
        .sort((a, b) => (b.p.x + b.p.y) - (a.p.x + a.p.y))[0]
      if (!outer) continue
      const station = at.get(outer.key) ?? { x: outer.p.x, y: outer.p.y, names: [] }
      station.names.push(g.label)
      at.set(outer.key, station)
    }
    return [...at]
  }, [full.groups, hidden, model.edges, placement])

  /* A NAME GOES WHERE NO TRACK POINTS (#2406 rogue census, label-collision).
     A name was always centred UNDER its station, which is right for a station
     on a horizontal track and wrong wherever a track leaves the station
     downwards: the line then runs straight through the middle of the name, and
     the halo breaks the stroke behind the letters, not between them. Measured
     on Metro: the vertical address line struck "Noorderlaan 98, Antwerpen",
     "Frankrijklei 5, Antwerpen" and the line names "Billing address" /
     "Located at"; the opportunities' spurs, which climb to their stations from
     below, struck "Q4 platform rollout" and "Warehouse analytics"; and the
     address line leaving the root struck "Cocco" itself.
     So each station's name takes the first place none of its own tracks
     points into, read off the first segment of every line at that station:
     BELOW while nothing leaves downwards (and, where the line's own name sits
     centred above, nothing climbs straight up through it); else ABOVE; else
     BESIDE, to the right; else below-LEFT. Metro only: its tracks are few and
     straight; a network edge meets its node from any angle, and moving those
     names is not this finding. */
  const namePlace = useMemo(() => {
    const out = new Map<string, NamePlace>()
    if (layout !== 'metro') return out
    type P = { x: number; y: number }
    const unit = (from: P, to: P) => {
      const len = Math.hypot(to.x - from.x, to.y - from.y)
      return len < 1 ? null : { x: (to.x - from.x) / len, y: (to.y - from.y) / len }
    }
    /* EVERY SEGMENT THAT TOUCHES THE STATION, not only the station's own
       edges: a line to a station further out is drawn straight THROUGH the
       ones before it with no vertex there (Metro's address line runs from the
       root through "Noorderlaan 98" to "Frankrijklei 5"), and a name placed
       from its own edges alone went back onto that line. Where the station is
       a segment's end the track leaves one way; where the segment passes
       through it, it leaves both ways. */
    const segments: [P, P][] = []
    for (const edge of model.edges) {
      const pts = placement.edges.get(edge.key)
      if (!pts) continue
      for (let i = 1; i < pts.length; i++) segments.push([pts[i - 1]!, pts[i]!])
    }
    const named = new Set(lineNames.map(([key]) => key))
    for (const [key, at] of placement.nodes) {
      const list: P[] = []
      for (const [a, b] of segments) {
        const ab = { x: b.x - a.x, y: b.y - a.y }
        const len2 = ab.x * ab.x + ab.y * ab.y
        if (len2 < 1) continue
        const tt = Math.max(0, Math.min(1, ((at.x - a.x) * ab.x + (at.y - a.y) * ab.y) / len2))
        if (Math.hypot(a.x + tt * ab.x - at.x, a.y + tt * ab.y - at.y) > 1.5) continue
        const toA = unit(at, a)
        const toB = unit(at, b)
        if (toA) list.push(toA)
        if (toB) list.push(toB)
      }
      if (list.length === 0) continue
      // A side is taken by a track leaving within ~53 degrees of it (a 45
      // degree spur counts); "straight up" means within ~25 degrees.
      const below = list.some((d) => d.y > 0.6)
      const above = list.some((d) => d.y < -0.6)
      const right = list.some((d) => d.x > 0.6)
      const straightUp = list.some((d) => d.y < -0.9)
      const place: NamePlace = !below && !(named.has(key) && straightUp) ? 'below'
        : !above ? 'above'
          : !right ? 'beside'
            : 'belowLeft'
      if (place !== 'below') out.set(key, place)
    }
    return out
  }, [layout, model.edges, placement, lineNames])

  const svgRef = useRef<SVGSVGElement>(null)
  const lineNamesRef = useRef(new Map<string, SVGTextElement>())
  const [measuredLineNames, setMeasuredLineNames] = useState<{
    placement: typeof placement; choices: Map<string, LineNamePlace>
  } | null>(null)
  const lineNamePlace = layout === 'metro' && measuredLineNames?.placement === placement
    ? measuredLineNames.choices : namePlace

  /* A line name is offset from its station, so a different track can cross it
     without touching the station at all. Keep station names on their clear ray;
     independently choose the first of the existing line-name positions whose
     actual glyph box clears EVERY drawn segment and the other record names. Pairing the two names forced
     otherwise clear line positions back onto tracks at dense interchanges.
     Without SVG measurement, the original ray choice remains the fallback. */
  useLayoutEffect(() => {
    if (layout !== 'metro') return
    let cancelled = false
    const measure = () => {
      if (cancelled || !svgRef.current) return
      type P = { x: number; y: number }
      type Box = { x: number; y: number; width: number; height: number }
      const segments: [P, P][] = []
      for (const points of placement.edges.values()) {
        for (let i = 1; i < points.length; i++) segments.push([points[i - 1]!, points[i]!])
      }
      const intersects = (box: Box, [a, b]: [P, P]) => {
        let from = 0; let to = 1
        const dx = b.x - a.x; const dy = b.y - a.y
        const clips: [number, number][] = [[-dx, a.x - box.x + 1], [dx, box.x + box.width + 1 - a.x],
          [-dy, a.y - box.y + 1], [dy, box.y + box.height + 1 - a.y]]
        for (const [p, q] of clips) {
          if (p === 0) { if (q < 0) return false }
          else {
            const at = q / p
            if (p < 0) from = Math.max(from, at)
            else to = Math.min(to, at)
            if (from > to) return false
          }
        }
        return true
      }
      const occupied: Box[] = []
      for (const text of svgRef.current.querySelectorAll<SVGTextElement>('g[data-node] > text')) {
        const station = placement.nodes.get(text.parentElement?.getAttribute('data-node') ?? '')
        if (!station || typeof text.getBBox !== 'function') continue
        try {
          const box = text.getBBox()
          if (box.width && box.height) occupied.push({ x: station.x + box.x, y: station.y + box.y, width: box.width, height: box.height })
        } catch { /* SVG measurement is unavailable in a unit-test DOM. */ }
      }
      const overlaps = (a: Box, b: Box) => a.x < b.x + b.width + 1 && b.x < a.x + a.width + 1
        && a.y < b.y + b.height + 1 && b.y < a.y + a.height + 1
      const choices = new Map<string, LineNamePlace>(namePlace)
      for (const [key, station] of lineNames) {
        const text = lineNamesRef.current.get(key)
        if (!text || typeof text.getBBox !== 'function' || typeof text.getComputedTextLength !== 'function') continue
        let box: DOMRect; let advance: number
        try { box = text.getBBox(); advance = text.getComputedTextLength() } catch { continue }
        if (!box.width || !box.height || !Number.isFinite(advance)) continue
        const style = getComputedStyle(text)
        const shift = (anchor: string) => anchor === 'middle' ? -advance / 2
          : (anchor === 'end') !== (style.direction === 'rtl') ? -advance : 0
        const bearing = box.x - Number(text.getAttribute('x') ?? 0) - shift(style.textAnchor)
        const top = box.y - Number(text.getAttribute('y') ?? 0)
        const preferred = namePlace.get(key) ?? 'below'
        const candidates: LineNamePlace[] = [preferred, ...(['below', 'above', 'beside', 'belowLeft', 'belowFar'] as LineNamePlace[])
          .filter((place) => place !== preferred)]
        const boxAt = (place: LineNamePlace): Box => {
          const at = lineNameAt(station, place)
          const anchor = place === 'beside' ? 'start' : place === 'belowLeft' ? 'end' : 'middle'
          return { x: at.x + bearing + shift(anchor), y: at.y + top, width: box.width, height: box.height }
        }
        const clear = candidates.find((place) => {
          const candidate = boxAt(place)
          return !segments.some((segment) => intersects(candidate, segment))
            && !occupied.some((other) => overlaps(candidate, other))
        })
        // Record names never move. Line choices are occupied in stable model
        // order, so the next line also clears the names already placed.
        occupied.push(boxAt(clear ?? preferred))
        // Never hide a name or change tracks when no existing position clears.
        if (clear && clear !== 'below') choices.set(key, clear)
        else if (clear === 'below') choices.delete(key)
      }
      setMeasuredLineNames((prev) => prev?.placement === placement && prev.choices.size === choices.size
        && [...choices].every(([key, place]) => prev.choices.get(key) === place) ? prev : { placement, choices })
    }
    measure()
    // Start every font remeasurement from the original ray map, not a previous
    // choice. That keeps fallback-font changes from cycling through positions.
    const fonts = document.fonts
    void fonts?.ready.then(measure)
    fonts?.addEventListener('loadingdone', measure)
    return () => { cancelled = true; fonts?.removeEventListener('loadingdone', measure) }
  }, [layout, placement, namePlace, lineNames])

  /* THE FRAME IS WHAT WAS DRAWN, not only where the stations are.
     The layout pads its stations by a fixed margin, but a LABEL is centred on
     its station and knows nothing of that margin: "Frankrijklei 5, Antwerpen"
     is wider than two margins, so its last letters fell outside the viewBox
     and the SVG clipped them. The drawing measures itself once it exists and
     the frame grows to hold it — it never shrinks below the layout's bounds, so
     a picture with short names is placed exactly where it always was. Where
     there is no layout engine (a unit test's DOM), the layout's bounds stand. */
  const [frame, setFrame] = useState<{ x: number; y: number; w: number; h: number } | null>(null)
  useLayoutEffect(() => {
    const svg = svgRef.current
    if (!svg || typeof svg.getBBox !== 'function') return
    let drawn: DOMRect
    try { drawn = svg.getBBox() } catch { return }
    if (!drawn.width || !drawn.height) return
    const margin = 8
    const x = Math.min(0, Math.floor(drawn.x - margin))
    const y = Math.min(0, Math.floor(drawn.y - margin))
    const w = Math.max(placement.bounds.width, Math.ceil(drawn.x + drawn.width + margin)) - x
    const h = Math.max(placement.bounds.height, Math.ceil(drawn.y + drawn.height + margin)) - y
    setFrame((prev) => (prev && prev.x === x && prev.y === y && prev.w === w && prev.h === h
      ? prev : { x, y, w, h }))
  }, [placement, lineNames, model, namePlace, lineNamePlace])

  const fx = frame?.x ?? 0
  const fy = frame?.y ?? 0
  const width = frame?.w ?? placement.bounds.width
  const height = frame?.h ?? placement.bounds.height

  /* A NAME IS WHOLE OR IT IS WITHHELD (#2406 rogue census, svg-text-clipped).
     The picture is drawn at reading size, and Metro's compass is wider than a
     record column (674 units in a 474px column on a desktop, a 294px one on a
     phone), so the canvas is a window onto it that a person pans. A name the
     window's edge crossed read as a broken word — "Noorderlaan 98, A…",
     "Billing addr…". Opening the map FITTED was measured and refused: the
     fitted scale is 0.70 on a desktop and 0.44 on a phone, which draws the
     kit's 11.5px names at 8px and 5px, below the smallest size the kit sets
     (--fs-2xs, 9.5px). So the map keeps its reading size and does what a map
     does at its viewport edge: a name that would be cut is not painted until a
     pan brings it wholly into view. Its station stays, carries the full name
     as its <title>, and the list beside the picture names every record — so
     nothing is withheld from anyone, only never drawn in half.
     Imperative on purpose: it follows every scroll, and a React state per
     scroll event would re-render the whole picture to flip one attribute. */
  const cull = useCallback(() => {
    const wrap = svgRef.current?.parentElement
    const svg = svgRef.current
    if (!wrap || !svg || wrap.clientWidth === 0) return
    const box = wrap.getBoundingClientRect()
    const left = box.left + wrap.clientLeft
    const top = box.top + wrap.clientTop
    const right = left + wrap.clientWidth
    const bottom = top + wrap.clientHeight
    for (const text of svg.querySelectorAll('text')) {
      const r = text.getBoundingClientRect()
      const whole = r.left >= left - 0.5 && r.right <= right + 0.5
        && r.top >= top - 0.5 && r.bottom <= bottom + 0.5
      if (whole) text.removeAttribute('data-culled')
      else text.setAttribute('data-culled', '')
    }
  }, [])
  // After every render: the names, the frame and the zoom each move them.
  useLayoutEffect(() => { cull() })
  useEffect(() => {
    const wrap = svgRef.current?.parentElement
    if (!wrap || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(cull)
    observer.observe(wrap)
    return () => observer.disconnect()
  }, [cull, layout])

  const tree = (
      /* A NESTED LIST, deliberately, and not a `role="tree"` widget.
          §7 asks for "a semantic accessible list/tree alternative"; a list
          of buttons satisfies it and is navigable by the browser's own tab
          order, which reaches every node and every verb without a
          hand-rolled roving-tabindex. A hand-built tree owes `aria-selected`
          on every item, arrow-key handling and an expansion model, and each
          of those is one edit away from announcing a state the DOM does not
          have — which is worse for the people relying on it than the plain
          list that always works. */
      <ul className={s.tree}
        aria-label={`Records connected to ${nodeLabel(full.root)}`}
        data-testid={`${testId}-tree`}>
        {outline.filter((group) => !hidden.has(group.slot)).map((group) => (
          <li key={group.slot}>
            <h4 className={s.groupName}>
              {group.label}
              {group.count !== null && (
                <span className={cx(s.legendN, 'tnum')}>
                  {' '}{group.capped ? `at least ${group.count}` : group.count}
                </span>
              )}
            </h4>
            {group.nodes.length === 0 ? (
              <span className={s.muted} data-testid={`${testId}-group-empty`}>
                {/* An honest empty state. `count === 0` is a measurement;
                    `count === null` is the absence of one, and saying
                    "none yet" for the second would state an absence nobody
                    took. */}
                {group.count === 0 ? 'None yet' : 'Not counted'}
              </span>
            ) : (
              <ul>
                {group.nodes.map((n) => (
                  <li key={n.key}>
                    <button type="button"
                      className={cx(s.treeNode, selected === n.key && s.treeOn)}
                      data-testid={`${testId}-tree-node`} data-node={n.key}
                      aria-pressed={selected === n.key}
                      onClick={() => select(n.key)}>
                      <EntityIcon iconKey={iconOf?.(n.entity) ?? 'record'} size={15} />
                      <span className={s.treeName}
                        data-anonymous={n.label?.trim() ? undefined : true}>
                        {nodeLabel(n)}
                      </span>
                      {n.depth > 1 && (
                        <span className={s.hops}>{n.depth} steps</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {group.more && onShowMore && (
              <Button type="button" size="sm" className={s.more}
                data-testid={`${testId}-more-${group.slot}`}
                onClick={() => onShowMore(group.slot)}>Show more</Button>
            )}
          </li>
        ))}
      </ul>
  )

  return (
    <section className={s.root} data-testid={testId} data-layout={layout}>
      <header className={s.head}>
        <span className={s.title}>Relationships</span>
        {/* The design's "9 connections · 7 types · 3 levels deep", from the
            door's own answers. See `counted` for why the first figure can be
            absent while the other two are not. */}
        <span className={s.summary} data-testid={`${testId}-summary`}>
          {counted && drawnGroups.length > 0 && (
            <span className="tnum">
              {drawnGroups.some((g) => g.capped) ? 'at least ' : ''}{connections} {connections === 1 ? 'connection' : 'connections'}
            </span>
          )}
          <span className="tnum">{drawnGroups.length} {drawnGroups.length === 1 ? 'type' : 'types'}</span>
          <span className="tnum">{deepest} {deepest === 1 ? 'level' : 'levels'} deep</span>
        </span>
        {/* The kit's own value slot. This was a hand-rolled input whose
            `border: var(--hair)` named a COLOUR token, so the shorthand reset
            its style to `none` and the field drew no edge at all — a search
            box that was a patch of white on white. */}
        <Input className={s.search} type="search" value={query}
          data-testid={`${testId}-search`}
          onChange={(e) => setQuery(e.currentTarget.value)}
          placeholder="Find in this graph"
          /* WITHIN THE LOADED NODES, and the placeholder says so. This component
             can reach no door, so a search that appeared to look further would
             be claiming a reach it does not have — the same distinction the
             Connections surface draws between its filter and the governed Add. */
          aria-label="Find a record among the ones this graph has loaded" />
        <span className={s.grow} />
        {actions}
        {/* Every open branch at once — one `onCollapse` per branch, so the host
            drops exactly what it would have dropped one at a time. Offered only
            while something is open. */}
        {onCollapse && full.expanded.length > 0 && (
          <Button type="button" size="sm" variant="ghost" data-testid={`${testId}-collapse-all`}
            onClick={() => {
              for (const key of full.expanded) {
                const node = full.nodes.get(key)
                if (node) onCollapse(node)
              }
            }}>Collapse all</Button>
        )}
        {onAddRelationship && (
          <Button type="button" size="sm" variant="primary" data-testid={`${testId}-add-relationship`}
            onClick={onAddRelationship}>Add relationship</Button>
        )}
        <div className={s.view} role="group" aria-label="Graph view">
          <Button type="button" size="sm" data-testid={`${testId}-zoom-out`}
            onClick={() => setZoom((z) => Math.max(0.4, Math.round((z - 0.2) * 10) / 10))}
            aria-label="Zoom out">−</Button>
          <Button type="button" size="sm" data-testid={`${testId}-fit`}
            onClick={() => setZoom(1)}>Fit</Button>
          <Button type="button" size="sm" data-testid={`${testId}-zoom-in`}
            onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.2) * 10) / 10))}
            aria-label="Zoom in">+</Button>
          {/* Only where the root has MOVED. The host knows which record the page
              is about; this component does not, and a control that was always
              on screen would be dead most of the time. */}
          {onResetRoot && (
            <Button type="button" size="sm" data-testid={`${testId}-reset-root`}
              onClick={onResetRoot}>Back to this record</Button>
          )}
        </div>
      </header>

      {problem && <p className={s.problem} data-testid={`${testId}-problem`}>{readableProblem(problem)}</p>}
      {busy && <p className={s.muted} data-testid={`${testId}-busy`}>Looking…</p>}

      {/* Every refusal, said out loud. §3: an honest refused state — and it
          names the group it refused rather than quietly drawing one group
          fewer, which is indistinguishable from an empty relationship. */}
      {full.refusals.length > 0 && (
        <ul className={s.refusals} data-testid={`${testId}-refusals`}>
          {full.refusals.map((r) => (
            <li key={r.slot}><b>{r.slot}</b> — {r.reason}</li>
          ))}
        </ul>
      )}

      {layout === 'levels' && levels ? (
        /* LEVELS: the key above the picture, as the design has it, then the
           picture, then the accessible tree — BELOW it and never behind a
           toggle (§7). The design itself carries no list; #2324 requires one,
           and below the picture is where it costs the picture nothing. */
        <div className={s.levelsBody}>
          <div className={s.strip} role="group" aria-label="Legend" data-testid={`${testId}-strip`}>
            <b className={s.stripTitle} aria-hidden="true">Legend</b>
            {/* The same group-around-a-list the side legend uses, and for the
                reason written there: `role="group"` on the list itself would
                orphan its items. */}
            <div role="group" aria-label="Relationship types" className={s.stripTypes}>
              <ul className={s.stripList} data-testid={`${testId}-legend`}>
                {outline.filter((g) => g.count !== 0 || g.nodes.length > 0 || hidden.has(g.slot)).map((group) => (
                  <li key={group.slot}>
                    <label className={s.chip} data-testid={`${testId}-legend-${group.slot}`}>
                      <input type="checkbox" checked={!hidden.has(group.slot)}
                        onChange={() => onToggleGroup?.(group.slot)}
                        disabled={!onToggleGroup} />
                      <EntityIcon iconKey={iconOf?.(full.groups.find((g) => g.slot === group.slot)?.target ?? '') ?? 'record'} size={13} />
                      <span className={s.legendName}>{group.label}</span>
                      {group.count !== null && (
                        <span className={cx(s.legendN, 'tnum')}>
                          {group.capped ? `at least ${group.count}` : group.count}
                        </span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            <span className={s.key}><span className={s.keyDirect} aria-hidden="true" />Direct</span>
            <span className={s.key}><span className={s.keyNested} aria-hidden="true" />Nested</span>
            {/* Only the states the loaded edges actually carry. A key for a dot
                nothing on screen draws would teach a reader to look for it. */}
            {full.edges.some((e) => cardStatus(e) === 'current') && (
              <span className={s.key} data-testid={`${testId}-key-current`}>
                <span className={s.keyCurrent} aria-hidden="true" />Current
              </span>
            )}
            {full.edges.some((e) => cardStatus(e) === 'ended') && (
              <span className={s.key} data-testid={`${testId}-key-ended`}>
                <span className={s.keyEnded} aria-hidden="true" />Ended
              </span>
            )}
          </div>
          <div ref={canvasRef} className={cx(s.canvasWrap, s.levelsCanvas)} data-testid={`${testId}-canvas`}
            tabIndex={0} role="region"
            aria-label={`Relationship graph canvas for ${nodeLabel(full.root)}`}>
            <RelationshipLevels
              placement={levels} model={model} full={full} selected={selected}
              dimmed={query.trim() === '' ? new Set() : new Set([...model.nodes.keys()].filter((k) => !matches.has(k)))}
              zoom={zoom} testId={testId} onSelect={select} verbsOf={verbsOf}
              iconOf={iconOf} describeFact={describeFact} canAdd={canAdd} onAdd={onAdd}
              onShowMore={onShowMore} onCollapse={onCollapse} nameOf={nodeLabel}
              label={`Relationship graph of ${nodeLabel(full.root)}, levels layout, ${model.nodes.size - 1} related records loaded`} />
          </div>
          <p className={s.treeTitle} aria-hidden="true">The same graph, as a list</p>
          {tree}
        </div>
      ) : (
      <div className={s.body}>
        {/* FOCUSABLE, because it SCROLLS. A dense graph is wider than its
            column, and axe's `scrollable-region-focusable` is the rule that
            says so: a region a pointer can scroll and a keyboard cannot is a
            part of the page keyboard users cannot reach. It went red the moment
            the "+N more" clusters pushed the canvas past its column, which is
            the check earning its place rather than a formality. */}
        <div className={s.canvasWrap} data-testid={`${testId}-canvas`}
          tabIndex={0} role="region" onScroll={cull}
          aria-label={`Relationship graph canvas for ${nodeLabel(full.root)}`}>
          <svg
            ref={svgRef}
            className={s.canvas}
            role="img"
            /* ONE accessible name for the picture, and the interactive
               alternative is the list beside it. A canvas that tried to be both
               ends up with a hundred tab stops nobody can navigate. */
            aria-label={`Relationship graph of ${nodeLabel(full.root)}, ${layout} layout, ${model.nodes.size - 1} related records loaded`}
            viewBox={`${fx} ${fy} ${width} ${height}`}
            style={{ width: `${Math.round(width * zoom)}px`, height: `${Math.round(height * zoom)}px` }}
          >
            <defs>
              <marker id={`${testId}-arrow`} viewBox="0 0 8 8" refX="7" refY="4"
                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0 0 L8 4 L0 8 z" className={s.arrowHead} />
              </marker>
            </defs>

            {model.edges.map((edge) => {
              const points = placement.edges.get(edge.key)
              if (!points) return null
              const line = lines.get(edge.slot) ?? 0
              return (
                <polyline key={edge.key}
                  className={cx(s.edge, selected && (edge.source === selected || edge.target === selected) && s.edgeOn)}
                  data-testid={`${testId}-edge`} data-slot={edge.slot} data-line={line}
                  points={points.map((p) => `${p.x},${p.y}`).join(' ')}
                  strokeDasharray={DASHES[line % DASHES.length]}
                  /* An ARROWHEAD only where the relationship declares a reading.
                     A symmetric partnership has no direction to draw, and drawing
                     one anyway would state a fact the model does not carry. */
                  markerEnd={edge.directed ? `url(#${testId}-arrow)` : undefined} />
              )
            })}

            {[...model.nodes.entries()].map(([key, node]) => {
              const at = placement.nodes.get(key)
              if (!at) return null
              const isRoot = key === 'root'
              const dim = query.trim() !== '' && !matches.has(key)
              return (
                <g key={key}
                  className={cx(s.node, isRoot && s.rootNode, selected === key && s.selected, dim && s.dim)}
                  data-testid={isRoot ? `${testId}-root` : `${testId}-node`}
                  data-node={key} data-depth={node.depth}
                  transform={`translate(${at.x} ${at.y})`}
                  onClick={() => select(key)}>
                  {/* The full name on the station itself, for the pointer that
                      finds a station whose name the canvas edge withheld. */}
                  <title>{nodeLabel(node)}</title>
                  <circle r={isRoot ? 13 : 9} className={s.dot} />
                  <text className={cx(s.nodeName, NAME_ANCHOR[namePlace.get(key) ?? 'below'])}
                    {...nameAt(namePlace.get(key) ?? 'below', isRoot)}>
                    {nodeLabel(node)}
                  </text>
                </g>
              )
            })}

            {/* THE CLUSTER at the end of a capped line (§3: "dense groups
                collapse into a labelled group node/cluster with an authorized
                visible count and show more").

                It is drawn one step OUTWARD from that line's outermost station,
                which keeps it on the line it belongs to without the layout
                having to know it exists — a synthetic node in the model would
                have made every assertion about "what is loaded" ambiguous, and
                the model is what the parity witness compares.

                The number is `count - loaded`, and it is drawn ONLY when the
                door measured a count. Where it did not, the affordance says
                "more" and no number at all: inventing one here would be the
                fabricated total every other surface in this platform refuses. */}
            {full.groups.filter((g) => g.more && !hidden.has(g.slot)).map((g) => {
              const stations = model.edges
                .filter((e) => e.slot === g.slot)
                .map((e) => placement.nodes.get(e.source === 'root' ? e.target : e.source))
                .filter((p): p is NonNullable<typeof p> => !!p)
              if (stations.length === 0) return null
              const root = placement.nodes.get('root')!
              const outer = stations.reduce((a, b) =>
                (Math.hypot(b.x - root.x, b.y - root.y) > Math.hypot(a.x - root.x, a.y - root.y) ? b : a))
              const dx = outer.x - root.x
              const dy = outer.y - root.y
              const len = Math.hypot(dx, dy) || 1
              const at = { x: outer.x + (dx / len) * 60, y: outer.y + (dy / len) * 60 }
              const rest = g.count === null ? null : Math.max(g.count - g.loaded, 0)
              return (
                <g key={`cluster-${g.slot}`} className={cx(s.node, s.cluster)}
                  data-testid={`${testId}-cluster`} data-slot={g.slot}
                  transform={`translate(${at.x} ${at.y})`}
                  onClick={() => onShowMore?.(g.slot)}>
                  <circle r={11} className={s.clusterDot} />
                  <text className={s.nodeName} x={0} y={26}>
                    {rest === null ? 'more' : `+${rest}${g.capped ? ' or more' : ''}`}
                  </text>
                </g>
              )
            })}

            {/* The LINE NAME at the outer end of each track. Meaning that does
                not depend on telling two strokes apart. */}
            {lineNames.map(([key, station]) => (
              <text key={key}
                ref={(text) => { if (text) lineNamesRef.current.set(key, text); else lineNamesRef.current.delete(key) }}
                className={cx(s.lineName, LINE_NAME_ANCHOR[lineNamePlace.get(key) ?? 'below'])}
                data-testid={`${testId}-line-name`} data-node={key}
                /* The line name chooses its own clear position at the station;
                   the record name keeps its original ray placement. */
                {...lineNameAt(station, lineNamePlace.get(key) ?? 'below')}>{station.names.join(' · ')}</text>
            ))}
          </svg>
        </div>

        {/* ── the same graph, as a tree ──────────────────────────────────── */}
        <div className={s.side}>
          {/* The group role is on a WRAPPER, not on the `<ul>`.
              FOUND BY AXE, and it is the kind of thing only a browser sees:
              `role="group"` on a list REPLACES its implicit `list` role, which
              orphans every `<li>` inside it — axe reported `listitem: 12` on
              exactly this element. A labelled group around the list keeps both
              the name and the list semantics. */}
          <div role="group" aria-label="Relationship types">
          <ul className={s.legend} data-testid={`${testId}-legend`}>
            {outline.map((group) => (
              <li key={group.slot}>
                <label className={s.legendRow} data-testid={`${testId}-legend-${group.slot}`}>
                  <input type="checkbox" checked={!hidden.has(group.slot)}
                    onChange={() => onToggleGroup?.(group.slot)}
                    disabled={!onToggleGroup} />
                  <span className={s.swatch} data-line={group.line} aria-hidden="true" />
                  <span className={s.legendName}>{group.label}</span>
                  {/* The count is the CALLER's. "at least" where the door
                      capped, nothing at all where it did not measure — a `0`
                      would claim the server looked. */}
                  {group.count !== null && (
                    <span className={cx(s.legendN, 'tnum')}>
                      {group.capped ? `at least ${group.count}` : group.count}
                    </span>
                  )}
                </label>
              </li>
            ))}
          </ul>
          </div>

          {tree}
        </div>
      </div>
      )}

      {/* ── the verbs, on the SELECTED node ─────────────────────────────────
          A bar rather than a popover: it is one tab stop away from the node
          that opened it, it cannot cover the node on a phone (§7), and it needs
          no focus trap. The node is NAMED in it, so the bar is never four
          unattached verbs. */}
      {layout !== 'levels' && selectedNode && verbs.length > 0 && (
        <div className={s.verbs} role="group" data-testid={`${testId}-verbs`}
          aria-label={`Actions for ${nodeLabel(selectedNode)}`}>
          <span className={s.verbsFor}>{nodeLabel(selectedNode)}</span>
          {verbs.map((verb) => (
            <Button key={verb.key} type="button" size="sm"
              data-testid={`${testId}-verb-${verb.key}`} onClick={verb.run}>
              {verb.label}
            </Button>
          ))}
          {/* WHAT THE EDGE SAYS (§3: "edge details expose governed role, purpose
              and validity when present"). Read off the edges the door already
              answered — this component resolves nothing and asks nothing — and
              shown only where the model actually carries something, because an
              empty "Role: —" is a field pretending to be a fact.

              One line per edge, named by its relationship, because a node on two
              lines has two sets of facts and merging them would attribute one
              relationship's role to another. */}
          {selectedEdges.map((edge) => {
            const detail = (edge.detail ?? {}) as {
              validFrom?: string | null; validTo?: string | null
              ended?: boolean; facts?: Record<string, unknown>
            }
            const facts = Object.entries(detail.facts ?? {})
              .filter(([, v]) => v != null && v !== '')
              .map(([k, v]) => `${k}: ${String(v)}`)
            const validity = [
              detail.validFrom ? `from ${detail.validFrom}` : null,
              detail.validTo ? `until ${detail.validTo}` : null,
              detail.ended ? 'ended' : null,
            ].filter(Boolean) as string[]
            if (facts.length === 0 && validity.length === 0) return null
            return (
              <span key={edge.key} className={s.edgeFacts}
                data-testid={`${testId}-edge-facts`} data-slot={edge.slot}>
                {edge.label}: {[...facts, ...validity].join(' · ')}
              </span>
            )
          })}
        </div>
      )}

      {peek && <div className={s.peek} data-testid={`${testId}-peek`}>{peek}</div>}
    </section>
  )
}

/* Re-declared beside the component whose props are written in it, so a host
   can type the data it hands this component from `@solum/ui` alone (#2406,
   design-sync kit pass: "hosts need @solum/experience to type their data").
   An alias, not a copy — the shape stays Experience's. */
export type GraphHop = GovernedGraphHop
